<template>
  <!-- Phase 2 - US-013: Fee Waiver Management -->
  <div>
    <b-card header="Fee Waiver Management" class="mb-3">
      <!-- Apply Waiver Button -->
      <div class="d-flex justify-content-end mb-3" v-if="isAdmin">
        <b-button variant="primary" size="sm" @click="openWaiverForm">
          <b-icon icon="plus"></b-icon> Apply New Waiver
        </b-button>
      </div>

      <!-- Waiver List -->
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner small></b-spinner> Loading...
      </div>
      <div
        v-else-if="waivers.length === 0"
        class="text-center py-3 text-muted"
      >
        No waiver records found.
      </div>
      <b-table
        v-else
        :items="waivers"
        :fields="fields"
        striped
        small
        responsive
        hover
      >
        <template #cell(sno)="{ index }">
          {{ index + 1 }}
        </template>
        <template #cell(waiver_percentage)="{ value }">
          {{ value }}%
        </template>
        <template #cell(original_amount)="{ value }">
          Rs. {{ Number(value || 0).toLocaleString("en-IN") }}
        </template>
        <template #cell(waived_amount)="{ value }">
          Rs. {{ Number(value || 0).toLocaleString("en-IN") }}
        </template>
        <template #cell(status)="{ value }">
          <b-badge
            :variant="
              value === 'APPROVED'
                ? 'success'
                : value === 'REJECTED'
                ? 'danger'
                : 'warning'
            "
          >
            {{ value }}
          </b-badge>
        </template>
        <template #cell(actions)="{ item }">
          <template v-if="item.status === 'PENDING' && isAdmin">
            <b-button
              size="sm"
              variant="outline-success"
              class="me-1"
              @click="updateStatus(item.waiver_id, 'APPROVED')"
            >
              Approve
            </b-button>
            <b-button
              size="sm"
              variant="outline-danger"
              @click="updateStatus(item.waiver_id, 'REJECTED')"
            >
              Reject
            </b-button>
          </template>
        </template>
      </b-table>
    </b-card>

    <!-- Waiver Application Modal -->
    <b-modal
      v-model="showForm"
      title="Apply Fee Waiver"
      centered
      hide-footer
      size="lg"
    >
      <form-wrapper @submit="submitWaiver">
        <b-row>
          <b-col cols="12" md="6">
            <input-field
              v-model.trim="form.reference_id"
              label="Patient / Hospital ID"
              rules="required"
            ></input-field>
          </b-col>
          <b-col cols="12" md="6">
            <select-field
              v-model="form.reference_type"
              label="Reference Type"
              :items="referenceTypes"
              rules="required"
            ></select-field>
          </b-col>
          <b-col cols="12" md="6">
            <select-field
              v-model="form.waiver_type"
              label="Waiver Type"
              :items="waiverTypes"
              rules="required"
            ></select-field>
          </b-col>
          <b-col cols="12" md="6">
            <input-field
              v-model="form.waiver_percentage"
              label="Waiver Percentage (%)"
              rules="required|integer"
              type="number"
            ></input-field>
          </b-col>
          <b-col cols="12" md="6">
            <input-field
              v-model="form.original_amount"
              label="Original Amount (Rs.)"
              rules="required|integer"
              type="number"
            ></input-field>
          </b-col>
          <b-col cols="12" md="6">
            <input-field
              :value="computedWaivedAmount"
              label="Waived Amount (Rs.)"
              disabled
            ></input-field>
          </b-col>
          <b-col cols="12" md="6">
            <select-field
              v-model="form.reason"
              label="Reason"
              :items="reasons"
              rules="required"
            ></select-field>
          </b-col>
          <b-col cols="12" md="6">
            <file-input-group
              v-model="form.document"
              accept=".jpg,.jpeg,.png,.pdf"
              rules="file-image-pdf"
              label="Supporting Document"
            ></file-input-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Remarks">
              <b-form-textarea
                v-model="form.remarks"
                rows="3"
                placeholder="Additional remarks..."
              ></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="d-flex justify-content-end mt-3">
          <b-button variant="outline-danger" @click="showForm = false"
            >Cancel</b-button
          >
          <b-button class="ms-3" variant="success" type="submit"
            >Submit Waiver</b-button
          >
        </div>
      </form-wrapper>
    </b-modal>
  </div>
</template>

<script>
import feeService from "../../assets/services/fee.service";
import hospitalService from "../../assets/services/hospital.service";
import SelectField from "../form/SelectField.vue";
import FileInputGroup from "../form/FileInputGroup.vue";

export default {
  name: "FeeWaiver",
  components: { SelectField, FileInputGroup },
  data() {
    return {
      isLoading: false,
      waivers: [],
      showForm: false,
      form: {
        reference_id: "",
        reference_type: "",
        waiver_type: "",
        waiver_percentage: 100,
        original_amount: 0,
        reason: "",
        remarks: "",
        document: null,
      },
      waiverTypes: [
        { name: "Full Waiver (100%)", value: "full" },
        { name: "Partial Waiver", value: "partial" },
      ],
      referenceTypes: [
        { name: "Hospital", value: "hospital" },
        { name: "Recipient", value: "recipient" },
      ],
      reasons: [
        { name: "Below Poverty Line (BPL)", value: "bpl" },
        { name: "Government Scheme", value: "government_scheme" },
        { name: "Special Case - Director Approval", value: "special_case" },
      ],
    };
  },
  computed: {
    fields() {
      let cols = [
        { key: "sno", label: "#" },
        { key: "reference_id", label: "Ref ID" },
        { key: "reference_type", label: "Ref Type" },
        { key: "waiver_type", label: "Waiver Type" },
        { key: "waiver_percentage", label: "Waiver %" },
        { key: "original_amount", label: "Original Amt" },
        { key: "waived_amount", label: "Waived Amt" },
        { key: "reason", label: "Reason" },
        { key: "status", label: "Status" },
        { key: "created_on", label: "Applied On" },
      ];
      if (this.isAdmin) {
        cols.push({ key: "actions", label: "Actions" });
      }
      return cols;
    },
    computedWaivedAmount() {
      let pct = parseFloat(this.form.waiver_percentage) || 0;
      let amt = parseFloat(this.form.original_amount) || 0;
      return Math.round((amt * pct) / 100);
    },
  },
  methods: {
    openWaiverForm() {
      this.form = {
        reference_id: "",
        reference_type: "",
        waiver_type: "",
        waiver_percentage: 100,
        original_amount: 0,
        reason: "",
        remarks: "",
        document: null,
      };
      this.showForm = true;
    },
    async loadWaivers() {
      try {
        this.isLoading = true;
        let hospital_id = this.user.hospital_id || "";
        let { data } = await feeService.getWaivers(hospital_id);
        this.isLoading = false;
        if (data.status && data.list) {
          this.waivers = data.list;
        }
      } catch (error) {
        this.isLoading = false;
        this.$alert.show(error.message);
      }
    },
    async submitWaiver() {
      try {
        this.$loader.show();
        let doc = { ...this.form };
        doc.waived_amount = this.computedWaivedAmount;

        // Upload document if provided
        if (doc.document instanceof File) {
          let body = new FormData();
          body.append("file", doc.document);
          let { data: uploadData } =
            await hospitalService.uploadContact(body);
          if (uploadData.status) {
            doc.document_path = uploadData.filePath_url;
          }
        }
        delete doc.document;

        let { data } = await feeService.applyWaiver(doc);
        this.$loader.hide();
        if (data.status) {
          this.$alert.show("Waiver submitted successfully");
          this.showForm = false;
          await this.loadWaivers();
        } else {
          this.$alert.show(data.msg);
        }
      } catch (error) {
        this.$loader.hide();
        this.$alert.show(error.message);
      }
    },
    async updateStatus(waiver_id, status) {
      try {
        let confirmed = await this.$confirm.show(
          `Are you sure you want to ${status.toLowerCase()} this waiver?`
        );
        if (!confirmed) return;
        this.$loader.show();
        let { data } = await feeService.updateWaiverStatus(waiver_id, status);
        this.$loader.hide();
        if (data.status) {
          this.$alert.show(`Waiver ${status.toLowerCase()} successfully`);
          await this.loadWaivers();
        } else {
          this.$alert.show(data.msg);
        }
      } catch (error) {
        this.$loader.hide();
        this.$alert.show(error.message);
      }
    },
  },
  mounted() {
    this.loadWaivers();
  },
};
</script>
